import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '../api'; // Ensure this points to your API client

// MUI Imports
import { Typography, Button, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Icons
import { fireworksIcon, redIcon } from '../icons'; 

const fireworksLocations = [
  { id: 'fw1', name: 'Sydney Harbour', lat: -33.8568, lng: 151.2153, time: '9pm & Midnight' },
  { id: 'fw2', name: 'Melbourne CBD (Docklands)', lat: -37.8150, lng: 144.9460, time: '9:30pm & Midnight' },
  { id: 'fw3', name: 'Brisbane (South Bank)', lat: -27.4772, lng: 153.0233, time: '8pm & Midnight' },
  { id: 'fw4', name: 'Perth (Elizabeth Quay)', lat: -31.9566, lng: 115.8575, time: '8pm & Midnight' },
  { id: 'fw5', name: 'Adelaide (Elder Park)', lat: -34.9196, lng: 138.5997, time: '9pm & Midnight' },
  { id: 'fw6', name: 'Geelong Waterfront', lat: -38.1439, lng: 144.3644, time: '9:30pm & Midnight' },
  { id: 'fw7', name: 'Ballarat (Lake Wendouree)', lat: -37.5501, lng: 143.8340, time: '9:30pm' },
];

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function FlyToLocation({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.flyTo(coords, 9); // Changed from 13 to 9 for a wider view
    }
  }, [coords, map]);
  return null;
}

function MapSearch({ searchQuery }) {
  const [position, setPosition] = useState([-25.2744, 133.7751]); 
  const [locationName, setLocationName] = useState('');
  const [displays, setDisplays] = useState([]); // <-- State for public displays
  
  const currentYear = new Date().getFullYear();

  // Fetch verified public displays on load
  useEffect(() => {
    const fetchVerifiedDisplays = async () => {
      try {
        const response = await api.get('/displays/verified');
        setDisplays(response.data); 
      } catch (err) {
        console.error("Error fetching displays for search map:", err);
      }
    };
    fetchVerifiedDisplays();
  }, []);

  // Handle geocoding search input
  useEffect(() => {
    if (!searchQuery) return;

    const geocode = async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          const { lat, lon, display_name } = data[0];
          setPosition([parseFloat(lat), parseFloat(lon)]);
          setLocationName(display_name);
        }
      } catch (err) {
        console.error("Geocoding error:", err);
      }
    };

    geocode();
  }, [searchQuery]);

  return (
    <MapContainer 
      center={position} 
      zoom={4} 
      style={{ height: '100%', width: '100%', zIndex: 1 }} 
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; OpenStreetMap contributors'
      />
      
      <FlyToLocation coords={position} />

      {locationName && (
        <Marker position={position}>
          <Popup>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Search Result:</Typography>
            <Typography variant="body2">{locationName}</Typography>
          </Popup>
        </Marker>
      )}

      {/* --- RENDER PUBLIC DISPLAY MARKERS --- */}
      {displays.map(display => (
        <Marker 
          key={display.id} 
          position={[parseFloat(display.latitude), parseFloat(display.longitude)]} 
          icon={redIcon}
        >
          <Popup>
            <Box sx={{ minWidth: 140 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {display.first_name ? `${display.first_name}'s Display` : 'Christmas Display'}
              </Typography>
              <Typography variant="body2">{display.address}</Typography>
              {display.photos && display.photos.length > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  📸 {display.photos.length} photo(s) available
                </Typography>
              )}
            </Box>
          </Popup>
        </Marker>
      ))}

      {/* --- RENDER FIREWORKS MARKERS --- */}
      {fireworksLocations.map(fw => (
        <Marker key={fw.id} position={[fw.lat, fw.lng]} icon={fireworksIcon}>
          <Popup>
            <Box sx={{ minWidth: 150 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>🎆 {fw.name}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Times: {fw.time}</Typography>
              <Button 
                variant="outlined" 
                size="small" 
                fullWidth
                component={RouterLink} 
                to={`/blog/new-years-eve-fireworks-${currentYear}`}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                View Full Event Guide
              </Button>
            </Box>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}

export default MapSearch;